import React from 'react';
import { Stack, Typography, Skeleton } from '@mui/material';
import styles from '../../assets/css/components/CryptoPrices.module.css';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import btcIcon from '/crypto/btc.svg';
import ethIcon from '/crypto/eth.svg';

type CryptoDelta = 'up' | 'down' | null;

interface CryptoPrice {
    price: string;
    delta: CryptoDelta;
}

interface CryptoPriceState {
    prices: Record<string, CryptoPrice>;
    sessionId: string | null;
}

// All supported cryptos:
//  [
//     'BTC', 'ETH', 'DOGE', 'XRP', 'LINK',
//     'DOT', 'ADA', 'LTC', 'SOL', 'BNB',
//     'TRX', 'XLM', 'AVAX', 'BCH', 'EGLD',
//     'ICP', 'ATOM'
// ];

const SUPPORTED_CRYPTOS = [
    'BTC', 'ETH'
];

const DEFAULT_CRYPTO_PRICES_WEBSOCKET_URL = 'wss://www.highstakes.com/websocket/';

const buildCryptoPricesWebSocketUrl = (): string => {
    const configuredUrl = import.meta.env.VITE_CRYPTO_PRICES_WEBSOCKET_URL || DEFAULT_CRYPTO_PRICES_WEBSOCKET_URL;

    try {
        const url = new URL(configuredUrl);
        if (url.protocol === 'http:') url.protocol = 'ws:';
        if (url.protocol === 'https:') url.protocol = 'wss:';
        return url.toString();
    } catch {
        return DEFAULT_CRYPTO_PRICES_WEBSOCKET_URL;
    }
};

export default class CryptoPrices extends React.Component<{}, CryptoPriceState> {
    private ws: WebSocket | null = null;

    state = {
        prices: {} as Record<string, CryptoPrice>,
        sessionId: null
    };

    componentDidMount() {
        this.initWebSocket();
    }

    componentWillUnmount() {
        if (this.ws) {
            this.ws.close();
        }
    }

    private initWebSocket = () => {
        this.ws = new WebSocket(buildCryptoPricesWebSocketUrl(), ['wamp']);

        this.ws.onopen = () => {
            if (this.ws) {
                this.ws.send(JSON.stringify([5, "crypto-exchange", {
                    "match": "exact"
                }]));
                this.startHeartbeat();
            }
        };

        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (Array.isArray(data)) {
                    const [messageType] = data;

                    if (messageType === 0) {
                        const [_, sessionId] = data;
                        this.setState({ sessionId }, () => {
                            if (this.ws) {
                                this.ws.send(JSON.stringify([5, "crypto-exchange", {
                                    "match": "exact"
                                }]));
                            }
                        });
                    }
                    else if (messageType === 8) {
                        const [_, topic, payload] = data;
                        if (topic === "crypto-exchange" && payload?.data) {
                            const { exchangeCurrency, exchangeBaseCurrency, value, valueChange } = payload.data;

                            if (SUPPORTED_CRYPTOS.includes(exchangeCurrency) && exchangeBaseCurrency === 'USD') {
                                const price = parseFloat(value).toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: exchangeBaseCurrency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                });
                                const delta = parseFloat(valueChange) >= 0 ? 'up' : 'down';

                                this.setState(prevState => ({
                                    prices: {
                                        ...prevState.prices,
                                        [exchangeCurrency]: {
                                            price,
                                            delta
                                        }
                                    }
                                }));
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error parsing websocket message:', error);
            }
        };

        this.ws.onclose = () => {
            this.setState({ sessionId: null });
            setTimeout(() => this.initWebSocket(), 5000);
        };
    };

    private startHeartbeat = () => {
        const sendHeartbeat = () => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify([6, "ping", {}]));
                setTimeout(sendHeartbeat, 10000);
            }
        };
        sendHeartbeat();
    };

    render() {
        const { prices } = this.state;
        const hasData = Object.keys(prices).length > 0;

        return (
            <Stack direction="row" alignItems="center" spacing={1}>
                {SUPPORTED_CRYPTOS.map(crypto => {
                    const cryptoData = prices[crypto];
                    const isLoading = !hasData || !cryptoData;

                    return (
                        <Stack spacing={1} p={1} key={crypto} className={styles.cryptoPriceBox}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {isLoading ? (
                                    <Skeleton variant="circular" width={18} height={18} />
                                ) : (
                                    <img src={crypto === 'BTC' ? btcIcon : ethIcon} alt={crypto} height={18} width={18} />
                                )}
                                {isLoading ? (
                                    <Skeleton variant="text" width={40} height={20} />
                                ) : (
                                    <Typography variant="body2" component="span" style={{ fontSize: '14px', fontWeight: 600 }}>{crypto}</Typography>
                                )}
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {isLoading ? (
                                    <>
                                        <Skeleton variant="circular" width={18} height={18} />
                                        <Skeleton variant="text" width={60} height={16} />
                                    </>
                                ) : (
                                    <>
                                        {cryptoData.delta && (
                                            <span className={`${styles.cryptoDelta} ${styles[cryptoData.delta]}`}>
                                                {cryptoData.delta === 'up' ? <KeyboardDoubleArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardDoubleArrowDownIcon sx={{ fontSize: 18 }} />}
                                            </span>
                                        )}
                                        <Typography variant="body2" component="span" style={{ fontSize: '12px', fontWeight: 600 }}>{cryptoData.price}</Typography>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        );
    }
}
